import { HugeiconsIcon } from "@hugeicons/react";
import { Calendar01Icon } from "@hugeicons-pro/core-bulk-rounded";

import { useState } from "react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverPopup, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface DatePickerProps {
  /** ISO `yyyy-MM-dd` (matches HTML <input type="date"> serialisation) */
  value?: string;
  onChange?: (value: string) => void;
  /** Hidden form-field name so this works inside FormData submissions */
  name?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Latest selectable day, ISO `yyyy-MM-dd`. */
  max?: string;
}

function toIsoDate(d: Date): string {
  // Local-time yyyy-MM-dd (avoids the UTC timezone shift you'd get from toISOString)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fromIsoDate(iso: string | undefined): Date | undefined {
  if (!iso) return undefined;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

export function DatePicker({
  value,
  onChange,
  name,
  placeholder = "Kies een datum",
  className,
  disabled,
  max,
}: DatePickerProps) {
  const latest = fromIsoDate(max);
  const [internal, setInternal] = useState<string | undefined>(value);
  const current = value ?? internal;
  const date = fromIsoDate(current);

  function commit(d: Date | undefined) {
    const next = d ? toIsoDate(d) : "";
    setInternal(next);
    onChange?.(next);
  }

  return (
    <>
      <Popover>
        <PopoverTrigger
          render={
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              className={cn(
                "h-9 w-full justify-start gap-2 px-3 text-sm font-normal",
                !date && "text-muted-foreground",
                className,
              )}
            >
              <HugeiconsIcon icon={Calendar01Icon} size={16} />
              {date ? format(date, "d MMM yyyy", { locale: nl }) : placeholder}
            </Button>
          }
        />
        <PopoverPopup className="p-2">
          <Calendar
            mode="single"
            selected={date}
            defaultMonth={date}
            onSelect={commit}
            disabled={latest ? { after: latest } : undefined}
            endMonth={latest}
            autoFocus
          />
        </PopoverPopup>
      </Popover>

      {/* Hidden input keeps FormData-based submission working unchanged */}
      {name && <input type="hidden" name={name} value={current ?? ""} />}
    </>
  );
}
